/**
 * Tabela de ProvisioningJobs.
 *
 * Suporta 2 tipos de jobs:
 *   - Full provisioning: tenant_spec.project_name (ex: "DIRECTPIN")
 *   - Partial: tenant_spec.partial_job_type (add_environment|add_sub_tenant|enable_saas)
 *     com tenant_name + environment/sub_tenant
 *
 * Polling: Tanstack Query no caller deve usar refetchInterval pra updates ao vivo.
 */

import { useEffect, useState } from 'react'
import { Table, Text, Group, Progress, Stack, Badge, Tooltip } from '@mantine/core'
import {
  IconUserCircle,
  IconCirclePlus,
  IconBuildingFactory2,
  IconCloud,
} from '@tabler/icons-react'
import type { ProvisioningJobResponse } from '@/api/awsOrgProvisioning'
import { JobStatusBadge } from './JobStatusBadge'

export interface ProvisioningJobsTableProps {
  jobs: ProvisioningJobResponse[]
  loading?: boolean
  onRowClick: (jobId: number) => void
}

export function ProvisioningJobsTable({
  jobs,
  loading,
  onRowClick,
}: ProvisioningJobsTableProps) {
  // Tick a cada 1s para updates de duração ao vivo em jobs em execução.
  const hasRunning = jobs.some((j) => j.status === 'running' || j.status === 'pending')
  useTickEverySecond(hasRunning)

  if (loading) {
    return (
      <Text size="sm" c="dimmed" data-testid="jobs-table-loading">
        Carregando…
      </Text>
    )
  }

  if (!jobs || jobs.length === 0) {
    return (
      <Text size="sm" c="dimmed" data-testid="jobs-table-empty">
        Nenhum job de provisionamento.
      </Text>
    )
  }

  return (
    <Table.ScrollContainer minWidth={900}>
      <Table striped highlightOnHover data-testid="provisioning-jobs-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>Alvo</Table.Th>
            <Table.Th>Operação</Table.Th>
            <Table.Th>Por</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Progresso</Table.Th>
            <Table.Th>Iniciado</Table.Th>
            <Table.Th>Duração</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map((job) => {
            const target = describeTarget(job)
            const op = describeOperation(job)
            const pctRaw =
              job.total_steps > 0
                ? (job.completed_steps / job.total_steps) * 100
                : 0
            const pct = Math.min(100, pctRaw)
            const isDone = job.status === 'completed'
            const isFailed = job.status === 'failed'
            const isRunning = job.status === 'running' || job.status === 'pending'

            return (
              <Table.Tr
                key={job.id}
                style={{ cursor: 'pointer' }}
                onClick={() => onRowClick(job.id)}
                data-testid={`job-row-${job.id}`}
              >
                <Table.Td>
                  <Text size="sm" fw={500} ff="monospace">
                    #{job.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2} miw={180}>
                    <Group gap={6} wrap="nowrap">
                      <target.icon size={14} stroke={1.7} />
                      <Text size="sm" fw={500} ff="monospace">
                        {target.label}
                      </Text>
                      {target.outcome?.length === 1 &&
                        !target.outcome[0].is_alive && (
                          <Tooltip
                            label="Conta foi removida/suspendida após a execução do job"
                            withArrow
                          >
                            <Badge
                              size="xs"
                              variant="light"
                              color="red"
                              style={{ textTransform: 'none' }}
                            >
                              removida
                            </Badge>
                          </Tooltip>
                        )}
                    </Group>
                    {target.outcome && target.outcome.length > 1 && (
                      <Group gap={4} wrap="wrap">
                        {target.outcome.map((acc) => (
                          <Tooltip
                            key={acc.account_name}
                            label={
                              acc.is_alive
                                ? 'Conta ativa em tenants'
                                : 'Conta removida/suspendida'
                            }
                            withArrow
                          >
                            <Badge
                              size="xs"
                              variant="light"
                              color={acc.is_alive ? 'teal' : 'red'}
                              style={{ textTransform: 'none', cursor: 'help' }}
                            >
                              {acc.account_name}
                            </Badge>
                          </Tooltip>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={op.color}
                    leftSection={<op.icon size={12} stroke={1.8} />}
                  >
                    {op.label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <CreatedByCell userId={job.created_by} />
                </Table.Td>
                <Table.Td>
                  <JobStatusBadge status={job.status} size="sm" />
                </Table.Td>
                <Table.Td>
                  <Stack gap={2} miw={140}>
                    <Group justify="space-between" gap={4}>
                      <Text size="xs" ff="monospace">
                        {isDone
                          ? `${job.total_steps}/${job.total_steps}`
                          : `${job.completed_steps}/${job.total_steps}`}
                      </Text>
                      <Text
                        size="xs"
                        c={isDone ? 'teal' : isFailed ? 'red' : 'dimmed'}
                        fw={isDone || isFailed ? 600 : 400}
                      >
                        {isDone ? '✓' : `${pct.toFixed(0)}%`}
                      </Text>
                    </Group>
                    <Progress
                      value={isDone ? 100 : pct}
                      size="xs"
                      color={isFailed ? 'red' : isDone ? 'teal' : 'blue'}
                      animated={isRunning}
                    />
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {formatDate(job.created_at)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">
                    {formatDuration(job.started_at, job.completed_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

interface TargetDescriptor {
  /** Nome principal exibido (account real ou plan label). */
  label: string
  icon: typeof IconBuildingFactory2
  /** Contas reais criadas + status (alive/removida) — exibidas como
   * subtitle pequeno embaixo do label. */
  outcome?: { account_name: string; is_alive: boolean }[]
}

/**
 * Resolve o que mostrar na coluna ALVO.
 *
 * Estratégia ULTRATHINK pra resolver definitivamente o caso de jobs com
 * mesmo plano mas outcomes diferentes (ex: job#2 = goandroid-dev-prd
 * REMOVIDA, job#3 = goandroid-prd VIVA — ambos planejados como
 * "goandroid · PRD"):
 *
 *   1. Se o backend retornou `created_accounts` (jobs novos) e tem 1
 *      account → usar `account_name` real como label primário, com badge
 *      "removida" se !is_alive
 *   2. Se >1 accounts (full provisioning ou sub-tenant multi-env) →
 *      label do plan + lista dos outcomes embaixo
 *   3. Sem `created_accounts` (job ainda em pending sem criar conta) →
 *      fallback no plan label (project_name ?? deriva do tenant_name)
 */
function describeTarget(job: ProvisioningJobResponse): TargetDescriptor {
  const spec = job.tenant_spec ?? {}
  const accounts = job.created_accounts ?? []

  // Caso 1: 1 conta criada — usa OUTCOME REAL como label
  if (accounts.length === 1) {
    const acc = accounts[0]
    return {
      label: acc.account_name,
      icon: spec.partial_job_type === 'add_environment'
        ? IconCloud
        : IconBuildingFactory2,
      outcome: [{ account_name: acc.account_name, is_alive: acc.is_alive }],
    }
  }

  // Caso 2: multiplas contas — label do plano + outcomes como subtitle
  // (cobre full provisioning multi-env, add_sub_tenant multi-env)
  const planLabel = describePlanLabel(spec)
  if (accounts.length > 1) {
    return {
      label: planLabel.label,
      icon: planLabel.icon,
      outcome: accounts.map((a) => ({
        account_name: a.account_name,
        is_alive: a.is_alive,
      })),
    }
  }

  // Caso 3: nenhuma conta ainda criada (job em pending/running antes do
  // create_account step) — fica no plano
  return planLabel
}

/** Label do plano original (fallback quando outcome ainda nao existe). */
function describePlanLabel(spec: ProvisioningJobResponse['tenant_spec']): {
  label: string
  icon: typeof IconBuildingFactory2
} {
  if (spec?.partial_job_type) {
    const base =
      spec.project_name ??
      derivedProjectName(spec.tenant_name, spec.environment) ??
      '—'
    if (spec.partial_job_type === 'add_environment') {
      return { label: `${base} · ${spec.environment ?? '?'}`, icon: IconCloud }
    }
    if (spec.partial_job_type === 'add_sub_tenant') {
      return {
        label: `${base} · ${spec.sub_tenant?.name ?? '?'}`,
        icon: IconBuildingFactory2,
      }
    }
    return { label: `${base} (SaaS)`, icon: IconBuildingFactory2 }
  }
  return { label: spec?.project_name ?? '—', icon: IconBuildingFactory2 }
}

/**
 * Strip env suffix (case-insensitive) do tenant_name pra recuperar o
 * project_name limpo. Usado pra display de jobs HISTÓRICOS criados antes
 * de v1.24.7 (que não persistiu project_name no tenant_spec).
 *
 * Ex: ('goandroid-dev', 'PRD') → 'goandroid' (strips '-dev', sufixo de env conhecido)
 *     ('goandroid', 'PRD') → 'goandroid' (sem sufixo, retorna como está)
 *
 * Os env aliases batem com `consoleai.org_sync_service.ENV_ALIASES`.
 */
function derivedProjectName(
  rawName?: string | null,
  _env?: string | null,
): string | null {
  if (!rawName) return null
  const knownEnvs = ['DEV', 'QA', 'HML', 'PRD', 'STG', 'SANDBOX']
  let name = rawName
  // Strip iterativo: trata casos tipo "goandroid-dev-prd" → "goandroid"
  let changed = true
  while (changed) {
    changed = false
    for (const env of knownEnvs) {
      const suffix = `-${env}`
      if (name.toUpperCase().endsWith(suffix)) {
        name = name.slice(0, -suffix.length)
        changed = true
        break
      }
    }
  }
  return name || rawName
}

function describeOperation(job: ProvisioningJobResponse): {
  label: string
  color: string
  icon: typeof IconCirclePlus
} {
  const t = job.tenant_spec?.partial_job_type
  if (t === 'add_environment')
    return { label: 'Adicionar ambiente', color: 'blue', icon: IconCirclePlus }
  if (t === 'add_sub_tenant')
    return { label: 'Adicionar sub-tenant', color: 'grape', icon: IconCirclePlus }
  if (t === 'enable_saas')
    return { label: 'Habilitar SaaS', color: 'orange', icon: IconCirclePlus }
  return { label: 'Provisionar tenant', color: 'teal', icon: IconBuildingFactory2 }
}

function CreatedByCell({ userId }: { userId?: string | null }) {
  if (!userId)
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    )
  return (
    <Tooltip label={`User ID ${userId}`} withArrow>
      <Group gap={4} wrap="nowrap">
        <IconUserCircle size={14} stroke={1.7} />
        <Text size="xs" c="dimmed" ff="monospace">
          #{userId}
        </Text>
      </Group>
    </Tooltip>
  )
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function formatDuration(
  startedAt?: string | null,
  completedAt?: string | null,
): string {
  if (!startedAt) return '—'
  const start = new Date(startedAt).getTime()
  const end = completedAt ? new Date(completedAt).getTime() : Date.now()
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '—'
  const sec = Math.floor((end - start) / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  const remSec = sec % 60
  if (min < 60) return `${min}m ${remSec}s`
  const hr = Math.floor(min / 60)
  const remMin = min % 60
  return `${hr}h ${remMin}m`
}

/** Hook que força re-render a cada 1s — só quando há jobs em execução. */
function useTickEverySecond(enabled: boolean) {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [enabled])
}
